import type { SceneName } from '@maria-cavalheiro-website/shared-types'

import type { PoemFigure } from './scene-art.ts'

/**
 * A figure for every stanza of every poem — the cast the interior illustrations
 * are drawn from.
 *
 * `scene-art.ts` draws the poem's cover; this draws its pages. Each entry is an
 * ordered list exactly as long as the poem has stanzas, and each figure carries,
 * in the comment above it, the line of that stanza it answers to. Those comments
 * are the point of the file: the mapping is a design decision about somebody's
 * book, and the only way to review it is to read the verse beside the choice.
 *
 * WHY A FIGURE FOR EVERY STANZA WHEN A POEM DRAWS TWO OR THREE. Since phase 0021
 * `poemLeaves` picks which stanzas are illustrated — the first, the last and one
 * between — and takes each picture from this cast at that stanza's own index. The
 * cast stays complete because it is the curation record, not the render list:
 * change the selection rule, or add a stanza, and the picture beside every chosen
 * line is still one somebody chose for that line rather than the nearest one left
 * over. Trimming it to the current three would make the next change a re-reading
 * of the poem.
 *
 * WHY IT EXISTS AT ALL. Twenty-three emblems for one hundred and ten stanzas meant
 * a poem was illustrated once and then ran on unaccompanied. *Pombas no telhado*
 * has six stanzas — the doves on the roof, their wings, their colours, the flock
 * over the houses, the nest, the chicks — and a single dove for all of it. A
 * reader who is six years old is reading the pictures as much as the words, and
 * there were no pictures after the first screen.
 *
 * THE POEMS ARE NOT TOUCHED BY THIS. `poem.stanzas` is verbatim transcribed
 * author content and carries no art metadata; the mapping is keyed by scene name
 * and stanza index from out here. `stanza-art.test.ts` asserts the two agree in
 * length for all twenty-three, which is what makes indexing safe without a
 * fallback arm.
 *
 * REPETITION WITHIN A POEM IS ALLOWED AND IS OFTEN RIGHT. *A Lua* names the moon
 * in three of its four stanzas; drawing something else to avoid a repeat would be
 * illustrating the poem we wish it were. Across poems it is commonplace — the
 * blossom appears in nine — and that is the collection rhyming with itself, which
 * is the same argument the plate's companions have always made.
 *
 * WHAT IS DRAWN IS THE STANZA'S OWN SUBJECT, not the poem's. That is why the
 * fifth stanza of *Cedinho no sítio* is a cup of coffee: the poem is about a
 * morning on a smallholding, but that stanza is about sitting down with a coffee
 * before walking out to the orchard.
 */

export const SCENE_STANZA_FIGURES: Readonly<Record<SceneName, readonly PoemFigure[]>> =
  Object.freeze({
    andorinhas: [
      // "Bandos velozes, se envolvendo no ar / Enchendo tudo, com seu piu"
      { codePoint: '1F426', annotation: 'bird' },
      // "De enfrentar o gavião, o pior inimigo."
      { codePoint: '1F985', annotation: 'eagle' },
      // "Nas alturas, seus cantos ecoam"
      { codePoint: '1F3B5', annotation: 'musical note' },
      // "Defendendo seus lares e ninhos."
      { codePoint: '1FABA', annotation: 'nest with eggs' },
      // "Parecem pérolas no céu azul."
      { codePoint: '2B50', annotation: 'star' },
      // "Atravessam, montanhas e até o mar"
      { codePoint: '1F30A', annotation: 'water wave' },
    ],

    arvores: [
      // "As árvores e suas folhas espalhadas / Nas beiradas das calçadas."
      { codePoint: '1F333', annotation: 'deciduous tree' },
      // "E uma joaninha desfilando a sua cor"
      { codePoint: '1F41E', annotation: 'lady beetle' },
      // "Com as suas folhas verdes e frias / Estão com o vento a balançar"
      { codePoint: '1F343', annotation: 'leaf fluttering in wind' },
      // "Os pássaros já estão posando, / Entre as flores e galhos enfeitando"
      { codePoint: '1F426', annotation: 'bird' },
      // "Árvores, vejo muita saúde e beleza"
      { codePoint: '1F33B', annotation: 'sunflower' },
    ],

    chuva: [
      // "Os pingos da chuva deslizam… / Pelas árvores como briza"
      { codePoint: '1F327', annotation: 'cloud with rain' },
      // "Virando num lamaçal / a quadra do arrozal."
      { codePoint: '1F33E', annotation: 'sheaf of rice' },
      // "Correm, relincham e estão a saltar"
      { codePoint: '1F434', annotation: 'horse face' },
      // "Os pássaros, quanta alegria!"
      { codePoint: '1F426', annotation: 'bird' },
      // "Caçam as formigas de asas / que saem de suas casas."
      { codePoint: '1F41C', annotation: 'ant' },
    ],

    cigarras: [
      // "As cigarras, tão faceiras cancioneiras"
      { codePoint: '1F997', annotation: 'cricket' },
      // "Pousam sobre os galhos e flores"
      { codePoint: '1F33C', annotation: 'blossom' },
      // "As cigarras cantoras e sanfoneiras"
      { codePoint: '1FA97', annotation: 'accordion' },
    ],

    escada: [
      // "Uma escada toda enfeitada, / Foi planejada para subir"
      { codePoint: '1FA9C', annotation: 'ladder' },
      // "A vista é linda: vejo os carros no asfalto."
      { codePoint: '1F697', annotation: 'automobile' },
      // "A lua surgiu no espaço, / A noite estendeu os braços" — the moon itself
      // belongs to *A Lua*, so the night it arrives in is drawn with a star.
      { codePoint: '2B50', annotation: 'star' },
      // "Até amanhã o sol surgir"
      { codePoint: '2600', annotation: 'sun' },
    ],

    gelado: [
      // "O dia está muito gelado, / O céu está tão nublado…"
      { codePoint: '1F976', annotation: 'cold face' },
      // "O gado não aguentou o frio, / E já foi se esconder…"
      { codePoint: '1F404', annotation: 'cow' },
      // "Lá no céu um arco-íris brilhou…"
      { codePoint: '1F308', annotation: 'rainbow' },
      // "O sol vai brilhar e nos aquecer."
      { codePoint: '2600', annotation: 'sun' },
    ],

    janela: [
      // "Através da janela que dá para o jardim"
      { codePoint: '1FA9F', annotation: 'window' },
      // "Vejo as borboletas a passear, / Pelas flores a pousar"
      { codePoint: '1F98B', annotation: 'butterfly' },
      // "E as joaninhas felizes passeando."
      { codePoint: '1F41E', annotation: 'lady beetle' },
    ],

    jardim: [
      // "Entre as flores coloridas quero morar"
      { codePoint: '1F33B', annotation: 'sunflower' },
      // "Uma roseira tão florida / Eu plantei no meu jardim"
      { codePoint: '1F339', annotation: 'rose' },
      // "O sabiá veio da floresta / E no jardim ele quer morar."
      { codePoint: '1F426', annotation: 'bird' },
      // "Um beija-flor, quanta alegria, / Todos os dias vem passear." Upstream has
      // no hummingbird, so the stanza is drawn by what it comes for.
      { codePoint: '1F33C', annotation: 'blossom' },
      // "E o vento entre as flores perfume a espalhar."
      { codePoint: '1F343', annotation: 'leaf fluttering in wind' },
    ],

    lua: [
      // "A lua brilha no horizonte"
      { codePoint: '1F319', annotation: 'crescent moon' },
      // "Ela vai lá desfilando / Até lá no imenso mar"
      { codePoint: '1F30A', annotation: 'water wave' },
      // "Até amanhã quando o sol nascer sorrindo."
      { codePoint: '2600', annotation: 'sun' },
      // "A lua entre as nuvens vai sumir"
      { codePoint: '2601', annotation: 'cloud' },
    ],

    mima: [
      // "Uma gata tão dengosa, / Aqui em casa apareceu"
      { codePoint: '1F431', annotation: 'cat face' },
      // "Que ela mostra já suas garrinhas."
      { codePoint: '1F43E', annotation: 'paw prints' },
      // "A escada quer subir"
      { codePoint: '1FA9C', annotation: 'ladder' },
      // "Ela ganhou uma casinha"
      { codePoint: '1F3E0', annotation: 'house' },
      // "Muitos elogios ela ganhou."
      { codePoint: '2764', annotation: 'red heart' },
    ],

    natureza: [
      // "Observe a natureza, / E veja o valor que tem"
      { codePoint: '1F33F', annotation: 'herb' },
      // "Olhe os pássaros voando"
      { codePoint: '1F426', annotation: 'bird' },
      // "Patos e muitos outros animais"
      { codePoint: '1F986', annotation: 'duck' },
      // "O colibri, o macaco, e o jabuti"
      { codePoint: '1F422', annotation: 'turtle' },
      // "Os peixes enchem os mares"
      { codePoint: '1F41F', annotation: 'fish' },
      // "Nos jardins, perfumadas flores… / A rosa, o cravo e a boca-de-leão."
      { codePoint: '1F339', annotation: 'rose' },
      // "Nesta vasta e viva natureza, / Ainda tem muitas riquezas"
      { codePoint: '1F30E', annotation: 'globe showing Americas' },
    ],

    noite: [
      // "A noite já está chegando, / Com um vestido muito lindo"
      { codePoint: '1F30C', annotation: 'milky way' },
      // "Sobre as casas da cidade"
      { codePoint: '1F3E0', annotation: 'house' },
      // "Colorindo as flores tão plenas"
      { codePoint: '1F33C', annotation: 'blossom' },
      // "Lá no alto, um avião passando…"
      { codePoint: '2708', annotation: 'airplane' },
      // "Está caindo os orvalhos / E as gotas estão brilhando sobre os galhos."
      { codePoint: '1F4A7', annotation: 'droplet' },
      // "O luar e as estrelas no infinito"
      { codePoint: '2B50', annotation: 'star' },
    ],

    palhaco: [
      // "Brinca, com o chapéu na mão… / Sorri com a cara pintada."
      { codePoint: '1F921', annotation: 'clown face' },
      // "A alegria da criançada / Que foram ver sua palhaçada."
      { codePoint: '1F388', annotation: 'balloon' },
      // "Ele leva no coração, / Uma boa recordação."
      { codePoint: '2764', annotation: 'red heart' },
      // "Nos faz rir do seu sapatão / Quando se enrosca e se joga no chão"
      { codePoint: '1F45F', annotation: 'running shoe' },
    ],

    pardais: [
      // "Os pardais estão em festa / No pé da jabuticabeira"
      { codePoint: '1F347', annotation: 'grapes' },
      // "Entoam uma canção, / Parecem que estão dizendo"
      { codePoint: '1F3B5', annotation: 'musical note' },
      // "Nos galhos se empoleirando, / Estão se espreguiçando"
      { codePoint: '1F426', annotation: 'bird' },
      // "Vieram de Portugal em navios, / Muitos dias, em mares bravos!"
      { codePoint: '26F5', annotation: 'sailboat' },
      // "À noite adormecem tão serenos…"
      { codePoint: '1FABA', annotation: 'nest with eggs' },
    ],

    pardaizinhos: [
      // "Os pardaizinhos são tão lindinhos / Estão aqui nos galhos, espertinhos"
      { codePoint: '1F424', annotation: 'baby chick' },
      // "Até nos telhados, já estão pulando"
      { codePoint: '1F3E0', annotation: 'house' },
      // "Caçam as formigas de asas"
      { codePoint: '1F41C', annotation: 'ant' },
      // "Agradeça a Deus, sempre que lembrar"
      { codePoint: '1F64F', annotation: 'folded hands' },
    ],

    passarinhos: [
      // "O canarinho fez seu ninho / Na varanda lá de casa."
      { codePoint: '1FABA', annotation: 'nest with eggs' },
      // "O sabiá estava cantando, / Veio lá do alto do morro."
      { codePoint: '1F426', annotation: 'bird' },
      // "Eles fazem uma orquestra, / Todos vem cantar aqui."
      { codePoint: '1F3BA', annotation: 'trumpet' },
      // "Os passarinhos com alegria, / Cantam muito, sem parar."
      { codePoint: '1F423', annotation: 'hatching chick' },
    ],

    pomar: [
      // "Deixando tudo se perder / Como um frio desanimado."
      { codePoint: '1F976', annotation: 'cold face' },
      // "Roga a Deus com insistência."
      { codePoint: '1F64F', annotation: 'folded hands' },
      // "Como um doce e lindo pombinho, / Nos braços da mãe, pequenino…"
      { codePoint: '1F54A', annotation: 'dove' },
      // "Agora depois de crescido / Cheio de graça e vontade"
      { codePoint: '1F333', annotation: 'deciduous tree' },
      // "E no pomar também colher / Os belos frutos no laranjal."
      { codePoint: '1F34A', annotation: 'tangerine' },
      // "Não deixe de olhar as flores / Que brotam com muito vigor"
      { codePoint: '1F33C', annotation: 'blossom' },
    ],

    pombas: [
      // "Elas são simples pombas, / Que estão ali no telhado"
      { codePoint: '1F54A', annotation: 'dove' },
      // "Suas asas, parecem leques, / Voam com muito vigor!"
      { codePoint: '1FABD', annotation: 'wing' },
      // "Tão linhas como as flores, / Delicadas, meigas e carinhosas."
      { codePoint: '1F33C', annotation: 'blossom' },
      // "Elas saem sobre as casas, / Em bandos a revoar."
      { codePoint: '1F3E0', annotation: 'house' },
      // "Fizeram um lindo ninho, / Em frente das minhas janelas…"
      { codePoint: '1FABA', annotation: 'nest with eggs' },
      // "Ali colocaram os ovinhos… / Logo nascem os pombinhos"
      { codePoint: '1F423', annotation: 'hatching chick' },
    ],

    quintal: [
      // "Uma planta chamada alecrim, / Outra planta chamada jasmim."
      { codePoint: '1F33F', annotation: 'herb' },
      // "Muitas sementes se espalham pelo chão, / Estão nascendo e muito lindas são"
      { codePoint: '1F331', annotation: 'seedling' },
      // "Coloquei bananas para os sanhaços"
      { codePoint: '1F34C', annotation: 'banana' },
      // "As abelhas estão sobre as florzinhas"
      { codePoint: '1F41D', annotation: 'honeybee' },
    ],

    romeu: [
      // "Romeu, Romeu. Onde você está?" — spoken by a cat, about a cat.
      { codePoint: '1F63B', annotation: 'smiling cat with heart-eyes' },
      // "Olhe aqui para a janela."
      { codePoint: '1FA9F', annotation: 'window' },
      // "Vou correr para te abraçar"
      { codePoint: '2764', annotation: 'red heart' },
      // "A lua já está indo embora"
      { codePoint: '1F319', annotation: 'crescent moon' },
    ],

    sereno: [
      // "O cavaleiro se levantou / E segue viagem pela antiga trilha."
      { codePoint: '1F434', annotation: 'horse face' },
      // "A relva está brilhando / cheia de gotas de orvalho."
      { codePoint: '1F4A7', annotation: 'droplet' },
      // "Uma borboleta colorida / Por cima da flor passeando."
      { codePoint: '1F98B', annotation: 'butterfly' },
      // "Deslizando, parece uma canoa… / Navegando num lago que é o ar"
      { codePoint: '1F6F6', annotation: 'canoe' },
      // "Muitas flores lindas a colher."
      { codePoint: '1F33C', annotation: 'blossom' },
    ],

    sitio: [
      // "A galinha com os pintinhos, / Pelas gramas saem ciscando"
      { codePoint: '1F414', annotation: 'chicken' },
      // "A vaca dá de mamar ao terneiro"
      { codePoint: '1F404', annotation: 'cow' },
      // "As lindas gotas de orvalho, / Vão logo se espalhando"
      { codePoint: '1F4A7', annotation: 'droplet' },
      // "O sol já está a brilhar / O dia está tão lindo"
      { codePoint: '2600', annotation: 'sun' },
      // "Tomar um café bem gostoso!"
      { codePoint: '2615', annotation: 'hot beverage' },
    ],

    sol: [
      // "O sol apareceu bem cedinho, / No telhado passou a colorir"
      { codePoint: '2600', annotation: 'sun' },
      // "Realçando o trigal dourado."
      { codePoint: '1F33E', annotation: 'sheaf of rice' },
      // "Enquanto a Terra está a girar."
      { codePoint: '1F30E', annotation: 'globe showing Americas' },
      // "As abelhinhas, tão eficientes… / Trabalham muito na polinização."
      { codePoint: '1F41D', annotation: 'honeybee' },
      // "Ele foi feito para aquecer, / A Terra, a Lua e o mar."
      { codePoint: '1F319', annotation: 'crescent moon' },
      // "Plantas e animais aguardam este presente!"
      { codePoint: '1F33F', annotation: 'herb' },
    ],
  })

/**
 * The figures for one poem's stanzas, in stanza order.
 *
 * The whole cast, indexed by stanza — `poemLeaves` is what decides which of them
 * a reader sees.
 *
 * Total by construction, for the same reason `emblemParts` is: `SceneName` is a
 * validated enum and the record is keyed over it, so the lookup cannot miss.
 */
export function stanzaFigures(name: SceneName): readonly PoemFigure[] {
  return SCENE_STANZA_FIGURES[name]
}
